import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, FileText, Trophy, ListTodo, X, Users, BookOpen, DollarSign, Paperclip, ExternalLink } from "lucide-react";
import type { Goal, Milestone, GoalPlanningItem, GoalUrlAttachment } from "@shared/schema";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import EditGoalModal from "@/components/edit-goal-modal";
import CreateNoteModal from "@/components/create-note-modal";
import LinkedNotesList from "@/components/linked-notes-list";
import DocumentUploadButton from "@/components/document-upload-button";
import MeasuredTrackingSection from "./measured-tracking-section";
import { useAuth } from "@/hooks/useAuth";
import { SortableMilestoneItem } from "./sortable-milestone-item";

interface GoalOverviewTabProps {
  goal: Goal;
}
export default function GoalOverviewTab({ goal }: GoalOverviewTabProps) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createNoteModalOpen, setCreateNoteModalOpen] = useState(false);
  const [createMilestoneDialogOpen, setCreateMilestoneDialogOpen] = useState(false);
  const { toast } = useToast();
  // Attachment mutations require edit permission server-side; shared view-only
  // recipients shouldn't see the controls at all.
  const { user: currentUser } = useAuth();
  const isGoalOwner = !!currentUser && currentUser.id === goal.userId;

  const { data: milestones = [], isLoading: milestonesLoading } = useQuery<Milestone[]>({
    queryKey: ["/api/goals", goal.id, "milestones"],
  });

  // Fetch planning items for read-only display
  const { data: planningItems = [] } = useQuery<GoalPlanningItem[]>({
    queryKey: ["/api/goals", goal.id, "planning-items"],
  });

  // Web link (URL) attachments via the shared Attach Document -> Enter URL flow
  const { data: urlAttachments = [] } = useQuery<GoalUrlAttachment[]>({
    queryKey: ["/api/goals", goal.id, "url"],
  });

  const deleteUrlAttachmentMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      await apiRequest("DELETE", `/api/goals/${goal.id}/url/${attachmentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "url"] });
      toast({ title: "Attachment removed" });
    },
    onError: () => {
      toast({ title: "Failed to remove attachment", variant: "destructive" });
    },
  });

  const handleAttachmentAdded = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "url"] });
  };

  // Create milestone form
  const createMilestoneSchema = z.object({
    title: z.string().min(1, "Title is required"),
    dueDate: z.string().optional(),
  });

  type CreateMilestoneFormInput = z.input<typeof createMilestoneSchema>;
  type CreateMilestoneFormData = z.output<typeof createMilestoneSchema>;

  const createMilestoneForm = useForm<CreateMilestoneFormInput, any, CreateMilestoneFormData>({
    resolver: zodResolver(createMilestoneSchema),
    defaultValues: {
      title: "",
      dueDate: "",
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const toggleMilestoneMutation = useMutation({
    mutationFn: async ({ id, achieved }: { id: string; achieved: boolean }) => {
      const response = await apiRequest("PATCH", `/api/milestones/${id}`, { achieved });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "milestones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: () => {
      toast({ title: "Couldn't update milestone", description: "Your change was not saved. Please try again.", variant: "destructive" });
    },
  });

  const deleteMilestoneMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/milestones/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "milestones"] });
    },
    onError: () => {
      toast({ title: "Couldn't delete milestone", description: "Please try again.", variant: "destructive" });
    },
  });

  const reorderMilestonesMutation = useMutation({
    mutationFn: async (newOrder: Array<{ id: string; displayOrder: number }>) => {
      await apiRequest("PATCH", `/api/goals/${goal.id}/milestones/reorder`, { milestones: newOrder });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "milestones"] });
    },
    onError: () => {
      toast({ title: "Couldn't reorder milestones", description: "Please try again.", variant: "destructive" });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = milestones.findIndex(m => m.id === active.id);
    const newIndex = milestones.findIndex(m => m.id === over.id);

    const newMilestones = [...milestones];
    const [moved] = newMilestones.splice(oldIndex, 1);
    newMilestones.splice(newIndex, 0, moved);

    const reorderedMilestones = newMilestones.map((m, index) => ({
      id: m.id,
      displayOrder: index,
    }));

    queryClient.setQueryData(["/api/goals", goal.id, "milestones"], newMilestones.map((m, index) => ({
      ...m,
      displayOrder: index,
    })));

    reorderMilestonesMutation.mutate(reorderedMilestones);
  };

  const handleToggleMilestone = (id: string, achieved: boolean) => {
    toggleMilestoneMutation.mutate({ id, achieved });
  };

  const handleDeleteMilestone = (id: string) => {
    deleteMilestoneMutation.mutate(id);
  };

  // Toggle goal achieved status mutation
  const isGoalAchieved = goal.status === "completed" || goal.progress === 100;
  
  const toggleAchievedMutation = useMutation({
    mutationFn: async () => {
      // Toggle between completed and active status
      const newStatus = isGoalAchieved ? "active" : "completed";
      const response = await apiRequest("PATCH", `/api/goals/${goal.id}`, { 
        status: newStatus,
        // Also set progress to 100 when achieving, or keep current when un-achieving
        ...(newStatus === "completed" ? { progress: 100 } : {})
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
      toast({
        title: isGoalAchieved ? "Goal marked as active" : "Goal achieved!",
        description: isGoalAchieved 
          ? "This goal has been moved back to active status." 
          : "Congratulations on achieving your goal!",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update goal status. Please try again.",
        variant: "destructive",
      });
    },
  });

  const createMilestoneMutation = useMutation({
    mutationFn: async (data: CreateMilestoneFormData) => {
      const payload: {title: string, dueDate?: Date} = {
        title: data.title,
      };
      
      if (data.dueDate) {
        payload.dueDate = new Date(data.dueDate);
      }
      
      const response = await apiRequest("POST", `/api/goals/${goal.id}/milestones`, payload);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "milestones"] });
      toast({
        title: "Milestone created",
        description: "Your milestone has been created successfully.",
      });
      setCreateMilestoneDialogOpen(false);
      createMilestoneForm.reset();
    },
    onError: (error: Error) => {
      console.error("Error creating milestone:", error);
      toast({
        title: "Error creating milestone",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleCreateMilestone = (data: CreateMilestoneFormData) => {
    createMilestoneMutation.mutate(data);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Goal Overview</h2>
        <div className="flex gap-2">
          <Button onClick={() => setCreateNoteModalOpen(true)} variant="outline" data-testid="button-create-note-goal">
            <FileText className="w-4 h-4 mr-2" />
            Create Note
          </Button>
          <Button 
            onClick={() => toggleAchievedMutation.mutate()} 
            variant="outline"
            disabled={toggleAchievedMutation.isPending}
            data-testid="button-mark-achieved"
            className={isGoalAchieved ? "bg-green-500/10 border-green-500/20 text-green-700 dark:text-green-300" : ""}
          >
            <Trophy className="w-4 h-4 mr-2" />
            {isGoalAchieved ? "Achieved ✓" : "Mark Achieved"}
          </Button>
          <Button onClick={() => setEditModalOpen(true)} variant="outline" data-testid="button-edit-goal">
            <Pencil className="w-4 h-4 mr-2" />
            Edit Goal
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{goal.progress}%</div>
            <Progress value={goal.progress} className="mt-2 h-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Target Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {goal.targetValue 
                ? goal.targetValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                : goal.customGoalTarget != null
                  ? goal.customGoalTarget.toLocaleString('en-US', { maximumFractionDigits: 2 })
                  : "—"}
            </div>
            {goal.customGoalTitle && (
              <p className="text-xs text-muted-foreground mt-1">{goal.customGoalTitle}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Current Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {goal.currentValue 
                ? (() => {
                    const isCurrency = goal.currentValue.startsWith('$');
                    const numericPart = goal.currentValue.replace(/[$,]/g, '');
                    const formattedNum = parseFloat(numericPart).toLocaleString('en-US', {
                      minimumFractionDigits: isCurrency ? 2 : 0,
                      maximumFractionDigits: 2
                    });
                    return isCurrency ? `$${formattedNum}` : formattedNum;
                  })()
                : "—"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Remaining</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600 dark:text-red-500">
              {(() => {
                if (!goal.customGoalTarget || goal.currentValueRaw === null || goal.currentValueRaw === undefined) {
                  return "—";
                }
                const remaining = goal.customGoalTarget - goal.currentValueRaw;
                if (goal.customGoalType === 'currency') {
                  return `$${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                }
                return remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              })()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Why Section - displayed prominently when goal has why reasons */}
      {(goal.whyReason1 || goal.whyReason2 || goal.whyReason3 || goal.whyReason4 || goal.whyReason5) && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="text-primary">💡</span> Why This Goal Matters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {goal.whyReason1 && (
                <div className="flex items-start gap-3" data-testid="text-why-reason-1">
                  <span className="text-primary font-bold">1.</span>
                  <p className="text-sm">{goal.whyReason1}</p>
                </div>
              )}
              {goal.whyReason2 && (
                <div className="flex items-start gap-3" data-testid="text-why-reason-2">
                  <span className="text-primary font-bold">2.</span>
                  <p className="text-sm">{goal.whyReason2}</p>
                </div>
              )}
              {goal.whyReason3 && (
                <div className="flex items-start gap-3" data-testid="text-why-reason-3">
                  <span className="text-primary font-bold">3.</span>
                  <p className="text-sm">{goal.whyReason3}</p>
                </div>
              )}
              {goal.whyReason4 && (
                <div className="flex items-start gap-3" data-testid="text-why-reason-4">
                  <span className="text-primary font-bold">4.</span>
                  <p className="text-sm">{goal.whyReason4}</p>
                </div>
              )}
              {goal.whyReason5 && (
                <div className="flex items-start gap-3" data-testid="text-why-reason-5">
                  <span className="text-primary font-bold">5.</span>
                  <p className="text-sm">{goal.whyReason5}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Description Section - separate from Why */}
      {goal.description && (
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground" data-testid="text-goal-description">
              {goal.description}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Milestones Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                Milestones
                <Badge variant="outline" data-testid="badge-milestone-count">
                  {milestones.filter(m => m.achieved).length} / {milestones.length} Complete
                </Badge>
              </CardTitle>
              <CardDescription>Track progress with milestones</CardDescription>
            </div>
            <Button 
              onClick={() => setCreateMilestoneDialogOpen(true)} 
              size="sm"
              data-testid="button-add-milestone"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Milestone
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {milestonesLoading ? (
            <div className="text-center py-4">
              <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : milestones.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No milestones yet. Edit the goal to add milestones.
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={milestones.map(m => m.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {milestones.map((milestone) => (
                    <SortableMilestoneItem
                      key={milestone.id}
                      milestone={milestone}
                      goalId={goal.id}
                      onToggle={handleToggleMilestone}
                      onDelete={handleDeleteMilestone}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Measured Tracking Section - only for custom measured goals */}
      {goal.progressCalculationType === 'custom' && (
        <MeasuredTrackingSection goal={goal} />
      )}

      {/* Goal Planning Section - Read-only display */}
      {planningItems.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="text-primary">📋</span> Goal Planning
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Who Do I Have to Know? */}
            {planningItems.filter(item => item.type === "who_know" && item.content).length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-primary" />
                  <h4 className="text-sm font-semibold">Who Do I Have to Know?</h4>
                </div>
                <div className="space-y-1 pl-6">
                  {planningItems.filter(item => item.type === "who_know" && item.content).map((item, index) => (
                    <div key={item.id} className="flex items-start gap-2" data-testid={`text-planning-who-${item.id}`}>
                      <span className="text-primary font-bold text-sm">{index + 1}.</span>
                      <p className="text-sm">{item.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* What Do I Need to Know? */}
            {planningItems.filter(item => item.type === "what_know" && item.content).length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-primary" />
                  <h4 className="text-sm font-semibold">What Do I Need to Know?</h4>
                </div>
                <div className="space-y-1 pl-6">
                  {planningItems.filter(item => item.type === "what_know" && item.content).map((item, index) => (
                    <div key={item.id} className="flex items-start gap-2" data-testid={`text-planning-what-know-${item.id}`}>
                      <span className="text-primary font-bold text-sm">{index + 1}.</span>
                      <p className="text-sm">{item.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* What Do I Have to Do? */}
            {planningItems.filter(item => item.type === "what_do" && item.content).length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ListTodo className="w-4 h-4 text-primary" />
                  <h4 className="text-sm font-semibold">What Do I Have to Do?</h4>
                </div>
                <div className="space-y-1 pl-6">
                  {planningItems.filter(item => item.type === "what_do" && item.content).map((item, index) => (
                    <div key={item.id} className="flex items-start gap-2" data-testid={`text-planning-what-do-${item.id}`}>
                      <span className="text-primary font-bold text-sm">{index + 1}.</span>
                      <p className="text-sm">{item.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resources and Budget */}
            {planningItems.filter(item => item.type === "resources_budget" && item.content).length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  <h4 className="text-sm font-semibold">Resources and Budget</h4>
                </div>
                <div className="space-y-1 pl-6">
                  {planningItems.filter(item => item.type === "resources_budget" && item.content).map((item, index) => (
                    <div key={item.id} className="flex items-start gap-2" data-testid={`text-planning-resources-${item.id}`}>
                      <span className="text-primary font-bold text-sm">{index + 1}.</span>
                      <p className="text-sm">{item.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Attachments (web links via the shared Attach Document flow) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              Attachments{urlAttachments.length > 0 ? ` (${urlAttachments.length})` : ""}
            </CardTitle>
            {isGoalOwner && (
              <DocumentUploadButton
                entityType="goal"
                entityId={goal.id}
                onAttachmentAdded={handleAttachmentAdded}
                urlOnly
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          {urlAttachments.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="text-no-goal-attachments">
              No attachments yet.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="goal-attachments-list">
              {urlAttachments.map((attachment) => (
                <Badge
                  key={attachment.id}
                  variant="outline"
                  className="gap-1 py-1 px-2 cursor-pointer hover:bg-muted"
                >
                  <ExternalLink className="h-3 w-3" />
                  <span className="text-xs max-w-[150px] truncate">{attachment.name}</span>
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  {isGoalOwner && (
                    <button
                      onClick={() => deleteUrlAttachmentMutation.mutate(attachment.id)}
                      className="ml-1 hover:text-destructive"
                      disabled={deleteUrlAttachmentMutation.isPending}
                      data-testid={`button-delete-attachment-${attachment.id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <LinkedNotesList
        entityType="goal"
        entityId={goal.id}
        showSearch={true}
        onCreateNote={() => setCreateNoteModalOpen(true)}
      />

      {/* Create Milestone Dialog */}
      <Dialog open={createMilestoneDialogOpen} onOpenChange={setCreateMilestoneDialogOpen}>
        <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Milestone</DialogTitle>
          </DialogHeader>

          <Form {...createMilestoneForm}>
            <form onSubmit={createMilestoneForm.handleSubmit(handleCreateMilestone)} className="space-y-4">
              <FormField
                control={createMilestoneForm.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter milestone title"
                        {...field}
                        data-testid="input-create-milestone-title"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={createMilestoneForm.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-create-milestone-due-date"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setCreateMilestoneDialogOpen(false);
                    createMilestoneForm.reset();
                  }}
                  data-testid="button-cancel-create-milestone"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMilestoneMutation.isPending}
                  data-testid="button-save-create-milestone"
                >
                  {createMilestoneMutation.isPending ? "Creating..." : "Create Milestone"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {editModalOpen && (
        <EditGoalModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          goal={goal}
        />
      )}

      <CreateNoteModal
        isOpen={createNoteModalOpen}
        onClose={() => setCreateNoteModalOpen(false)}
        entityType="goal"
        entityId={goal.id}
        entityTitle={goal.title}
      />
    </div>
  );
}
